import { useState } from "react";
import { GamesDonePop } from "../../components/shared/GamesDonePop";
import { nfmt, useGamesStats } from "../../hooks/gamesStats";
import type { DateRange } from "../../components/DateSelector";
import { fmtDateApi, toBusinessDate } from "../../utils/date";
import css from "./board.module.css";

/**
 * «База → Игры» (11.09, владелец): «чистое количество отведённых игр за период,
 * который настраивается стандартным календарём».
 *
 * Считает сервер тем же запросом, что и финансы ЕО, — чтобы цифры не расходились:
 * основной отчёт, не архивный, бронь не отменена, сумма больше нуля. Всё остальное
 * (отмены, переносы, блоки, заявки, игры без отчёта) сюда не попадает.
 *
 * 20.09 (владелец): «крупно показывать, сколько игр отведено и сколько забронировано
 * на будущее, чтобы ОО понимал, сколько всего игр прогнозируется в этом месяце» —
 * три числа в ряд: отведено · впереди · прогноз (их сумма).
 */
const nf = nfmt;

export function OoGamesCount({ range }: { range: DateRange }) {
  const from = range.start ? fmtDateApi(toBusinessDate(range.start)) : null;
  const to = range.end ? fmtDateApi(toBusinessDate(range.end)) : null;
  /* 26.10 (владелец: «правильно ли отображается количество отведённых игр… по клику — дропдаун с подробной статистикой»):
     числа — из /api/finance/games-stats (то же «отведено», что у финансов ЕО). Раньше «Прошедшие дни без отчёта» считались
     вычитанием и показывали учебные игры, а «Из них отменено» — не входило в «Броней в периоде». */
  const { data: s, error } = useGamesStats(from, to);
  const [pop, setPop] = useState<HTMLElement | null>(null);

  if (!from || !to) return <div className={css.gcWrap}><div className={css.gcHint}>Выберите период календарём сверху.</div></div>;
  if (error) return <div className={css.gcWrap}><div className={css.gcHint}>Ошибка: {error}</div></div>;
  if (!s) return <div className={css.gcWrap}><div className={css.gcHint}>Считаю…</div></div>;

  const p = s.period;
  const conducted = s.games;
  const ahead = p.ahead + p.todayPending;   // «впереди» — как раньше: с сегодня, отчёта ещё нет (без учебных)
  const forecast = p.forecast;
  const noReport = p.pastNoReport.length + p.zeroReport.length;
  const quests = s.byQuest.filter((q) => q.games > 0);

  return (
    <div className={css.gcWrap}>
      <div className={css.gcPeriod}>{from.split("-").reverse().join(".")} — {to.split("-").reverse().join(".")}</div>
      <div className={css.gcTriple}>
        <div className={css.gcCell}>
          <button type="button" className={`${css.gcBig} ${css.gcTap}`} title="Подробно об отведённых играх"
            onClick={(e) => { const el = e.currentTarget; setPop((cur) => (cur ? null : el)); }}>{conducted}</button>
          <div className={css.gcLabel}>отведено</div>
        </div>
        <div className={css.gcCell}>
          <div className={`${css.gcBig} ${css.gcAhead}`}>{ahead}</div>
          <div className={css.gcLabel}>впереди</div>
        </div>
        <div className={css.gcCell}>
          <div className={`${css.gcBig} ${css.gcForecast}`}>{forecast}</div>
          <div className={css.gcLabel}>прогноз</div>
        </div>
      </div>
      <div className={css.gcHint}>
        <b>Прогноз</b> = отведено + брони впереди (с сегодня до конца периода, отчёта по ним ещё нет; учебные не в счёте).
        Нажмите на число отведённых — подробная статистика.
        Отведено — игры с отчётом основного оператора и суммой больше нуля; отмены, переносы,
        блоки и заявки сюда не входят.
      </div>

      <div className={css.gcRows}>
        <div className={css.gcRow}><span>Выручка по этим играм</span><b>{nf(s.revenue)} ₽</b></div>
        <div className={css.gcRow}><span>Броней в периоде</span><b>{p.booked}</b></div>
        <div className={css.gcRow}><span>Отменено (в брони выше не входят)</span><b>{p.cancelled}</b></div>
        {p.learning.length ? <div className={css.gcRow}><span>Из них учебных (не в «отведено»)</span><b>{p.learning.length}</b></div> : null}
        {noReport > 0 ? <div className={css.gcRow}><span>Прошли без отчёта</span><b>{noReport}</b></div> : null}
        <div className={css.gcRow}><span>Игроков по броням</span><b>{p.bookedPlayers}</b></div>
      </div>

      {quests.length ? (
        <>
          <div className={css.gcSub}>По квестам</div>
          <div className={css.gcRows}>
            {quests.map((q, i) => (
              <div key={`${q.name}-${i}`} className={css.gcRow}>
                <span>{q.name || "—"}</span>
                <b>{q.games}</b>
              </div>
            ))}
          </div>
        </>
      ) : null}
      <GamesDonePop open={!!pop} anchor={pop} stats={s} onClose={() => setPop(null)} money={false} />
    </div>
  );
}
